<template>
  <div :class="['contact-card', checkUnread]" @click="openContactPage()">
    <!-- Desktop layout -->
    <div class="desktop-layout">
      <!-- First row: Company name -->
      <div class="row-1">
        <div class="company-name">{{ contact.company.name }}</div>
      </div>
      
      <!-- Second row: Sender name (left) and Date + Response (right) -->
      <div class="row-2 d-flex justify-content-between align-items-start">
        <div class="sender-name">{{ contact.sender }}</div>
        <div class="date-response-container d-flex flex-column align-items-center">
          <div class="date">{{ formatDate }}</div>
          <div :class="['user-response-badge', checkResponseClass]">{{ userResponse }}</div>
          <div v-if="showFileUploadRequest" class="file-upload-text" @click.stop="openFileUploadPopup">
            必要書類を提出してください ≫
          </div>
        </div>
      </div>
      
      <!-- Third row: Subject -->
      <div class="row-3">
        <div class="subject">{{ contact.subject }}</div>
      </div>
    </div>

    <!-- Mobile layout -->
    <div class="mobile-layout">
      <!-- First row: Company name -->
      <div class="mobile-row-1">
        <div class="company-name">{{ contact.company.name }}</div>
      </div>
      
      <!-- Second row: Sender name -->
      <div class="mobile-row-2">
        <div class="sender-name">{{ contact.sender }}</div>
      </div>
      
      <!-- Third row: Subject -->
      <div class="mobile-row-3">
        <div class="subject">{{ contact.subject }}</div>
      </div>
      
      <!-- Fourth row: Date (left) and Response badge (right) -->
      <div class="mobile-row-4">
        <div class="mobile-date-response-wrapper">
          <div class="date">{{ formatDate }}</div>
          <div :class="['user-response-badge', checkResponseClass]">{{ userResponse }}</div>
        </div>
        <!-- File upload text below on its own line, right-aligned -->
        <div v-if="showFileUploadRequest" class="file-upload-text" @click.stop="openFileUploadPopup">
          必要書類を提出してください ≫
        </div>
      </div>
    </div>
    
    <!-- Contact Confirmation Modal -->
    <ContactConfirmation
      :contact="contact"
      :questionRequest="false"
      :refrain="false"
      @redirectToContactList="redirectToContactList"
      v-if="showConfirmationModal"
    />
  </div>
</template>
  
  <script>
import moment from 'moment';
import ContactConfirmation from './ContactConfirmation.vue';
  export default {
    components: {
      ContactConfirmation,
    },
    props: {
      contact: {
        type: Object,
        required: true,
      },
      profile: {
        type: Object,
        default: () => ({}),
      },
    },
    data() {
      return {
        showConfirmationModal: false,
      };
    },
    computed: {
        formatDate(){
            return moment(this.contact.sent_date).format("YYYY/MM/DD");
        },
        checkUnread() {
            if(this.contact.is_read) {
                return 'read'
            } else {
                return 'unread'
            }
        },
        userResponse() {
            if (this.contact.contact_type === 'その他通知') {
                return '(回答不要)';
            }
            
            const response = this.contact.user_response || '未回答';
            
            // Map user response values
            const responseMapping = {
                '応募見合わせ': '見合わせ',
                'カジュアル面談': '面談参加回答済',
                '面接参加': '面接参加回答済',
                '選考応募': '応募済'
            };
            
            return responseMapping[response] || response;
        },
        checkResponseClass() {
            const response = this.userResponse;
            return response === '未回答' ? 'unreplied' : 'replied';
        },
        showFileUploadRequest() {
            // Check if file upload request exists and has items
            const hasFileUploadRequest = this.contact.file_upload_request && 
                                        this.contact.file_upload_request.length > 0;
            
            if (!hasFileUploadRequest) return false;
            
            // Check if user response is one of: Apply, Attend interview, or Attend casual interview
            const validResponses = ['選考応募', '面接参加', 'カジュアル面談'];
            const hasValidResponse = this.contact.user_response && 
                                    validResponses.includes(this.contact.user_response);
            
            if (!hasValidResponse) return false;
            
            // Check if requested files have been uploaded in profile
            // file_upload_request contains items like "職務経歴書" (resume) or "履歴書" (cv)
            const requestedFiles = this.contact.file_upload_request;
            const profile = this.profile || {};
            
            // Check if all requested files are uploaded
            const allFilesUploaded = requestedFiles.every(fileType => {
                if (fileType === '職務経歴書') {
                    // Check if resume is uploaded
                    return profile.resume && profile.resume !== '';
                } else if (fileType === '履歴書') {
                    // Check if CV is uploaded
                    return profile.cv && profile.cv !== '';
                }
                return false;
            });
            
            // Show text only if files have NOT been uploaded
            return !allFilesUploaded;
        },
    },
    methods: {
        openContactPage(){
            const id = this.contact._id;
            this.$router.push({
                name: 'contactDetails',
                params: { id },
            })
        },
        openFileUploadPopup(){
            // Show confirmation modal when clicking file upload text
            this.showConfirmationModal = true;
        },
        redirectToContactList(hasFileRequest) {
            this.showConfirmationModal = false;
            if (hasFileRequest) {
                this.$router.push({ name: 'userProfile', query: { uploadFile: 'true' }});
            }
            // Stay on contact list page if no file request
        },
    },
  };
  </script>
  
  <style scoped>
.contact-card {
  background-color: white;
  border: 1px solid #e0e0e0;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  border-radius: 10px;
  padding: 15px 20px;
  margin-bottom: 12px;
}

.contact-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.unread {
  background-color: white;
}

.unread .company-name,
.unread .sender-name,
.unread .date,
.unread .subject {
  font-weight: bold;
}

.read {
  background-color: white;
}

.read .company-name,
.read .sender-name,
.read .date,
.read .subject {
  font-weight: normal;
}

/* Desktop Layout (default) */
.desktop-layout {
  display: flex;
  flex-direction: column;
}

.mobile-layout {
  display: none;
}

.row-1 {
  margin-bottom: 8px;
}

.row-2 {
  margin-bottom: 8px;
}

.row-3 {
  margin-top: 0;
}

.company-name {
  font-weight: 600;
  font-size: 18px;
  color: #2F2E41;
}

.unread .company-name {
  font-weight: bold;
}

.read .company-name {
  font-weight: normal;
}

.sender-name {
  font-size: 14px;
  color: #2F2E41;
  flex: 1;
  min-width: 0;
}

.date-response-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  min-width: 200px;
}

.date {
  font-size: 14px;
  color: #2F2E41;
  white-space: nowrap;
  font-weight: bold;
  text-align: center;
  width: 100%;
}

.user-response-badge {
  min-width: 150px;
  height: 30px;
  font-weight: bold;
  text-align: center;
  font-size: 12px;
  padding: 5px 15px;
  border-radius: 20px;
  white-space: nowrap;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #E5E7EB;
  border: 1px solid #E0E7FF;
}

.user-response-badge.unreplied {
  color: #483ACA;
}

.user-response-badge.replied {
  color: #515C6B;
}

.file-upload-text {
  font-size: 12px;
  color: #DD2A29;
  cursor: pointer;
  margin-top: 4px;
  white-space: nowrap;
  text-decoration: none !important;
  text-align: center;
  width: 100%;
}

.file-upload-text:hover {
  opacity: 0.8;
}

.subject {
  font-size: 14px;
  color: #2F2E41;
  word-wrap: break-word;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Mobile Layout */
@media (max-width: 767px) {
  .contact-card {
    padding: 15px 16px;
    border-radius: 10px;
    box-shadow: 0px 4px 4px 0px #00000040;
  }

  .desktop-layout {
    display: none;
  }

  .mobile-layout {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .mobile-row-1,
  .mobile-row-2,
  .mobile-row-3,
  .mobile-row-4 {
    display: flex;
    align-items: center;
  }

  .mobile-row-1 {
    flex-shrink: 0;
  }

  .mobile-row-2 {
    flex-shrink: 0;
  }

  .mobile-row-3 {
    flex: 1;
    min-height: 0;
  }

  .mobile-row-4 {
    flex-shrink: 0;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .mobile-date-response-wrapper {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
  }

  .mobile-layout .company-name {
    font-size: 16px;
    font-weight: 600;
    color: #2F2E41;
  }

  .unread .mobile-layout .company-name {
    font-weight: bold;
  }

  .read .mobile-layout .company-name {
    font-weight: normal;
  }

  .mobile-layout .sender-name {
    font-size: 14px;
    color: #2F2E41;
  }

  .mobile-layout .date {
    font-size: 12px;
    color: #2F2E41;
    font-weight: bold;
  }

  .unread .mobile-layout .company-name,
  .unread .mobile-layout .sender-name,
  .unread .mobile-layout .date,
  .unread .mobile-layout .subject {
    font-weight: bold;
  }

  .read .mobile-layout .company-name,
  .read .mobile-layout .sender-name,
  .read .mobile-layout .date,
  .read .mobile-layout .subject {
    font-weight: normal;
  }

  .mobile-layout .user-response-badge {
    font-size: 10px;
    height: 26px;
    padding: 5px 70px;
    border-radius: 15px;
  }

  .mobile-layout .file-upload-text {
    font-size: 10px;
    margin-top: 0;
    text-align: right;
    width: 100%;
  }

  .mobile-layout .subject {
    font-size: 14px;
    color: #2F2E41;
  }
}

/* Desktop Layout */
@media (min-width: 768px) {
  .contact-card {
    border-radius: 10px;
    box-shadow: 0px 4px 4px 0px #00000040;
  }
  
  .desktop-layout {
    display: flex;
    flex-direction: column;
  }

  .mobile-layout {
    display: none;
  }

  .date-response-container {
    gap: 10px;
  }
}
  </style>